<script setup>
import { formatDate } from '@core/utils/formatters'
import AppointmentsCalendar from '@/components/scheduling/AppointmentsCalendar.vue'
import SupervisedWorkoutRecordingDialog from '@/components/appointments/SupervisedWorkoutRecordingDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import { weekRangeIso } from '@/stores/availability'
import {
  appointmentStatusColor,
  appointmentStatusLabel,
  canCancel,
  canComplete,
  canConfirm,
  canMarkNoShow,
  canReschedule,
  canUpdateSessionFormat,
  classificationLabel,
  sessionFormatLabel,
  useAppointmentsStore,
} from '@/stores/appointments'
import { sessionFormatColor } from '@/stores/clientBooking'
import {
  ENGAGEMENT_KINDS,
  observationKindLabel,
} from '@/stores/behaviourObservations'
import {
  canRespondToRisk,
  riskAlertType,
  useNoShowRiskAssessmentsStore,
} from '@/stores/noShowRiskAssessments'

definePage({
  meta: {
    navActiveLink: 'appointments',
  },
})

const store = useAppointmentsStore()
const riskStore = useNoShowRiskAssessmentsStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const detailDialog = ref(false)
const cancelDialog = ref(false)
const rescheduleDialog = ref(false)
const completeDialog = ref(false)
const riskDialog = ref(false)
const selected = ref(null)
const selectedRisk = ref(null)
const editSessionFormat = ref(null)
const cancelPreview = ref(null)
const cancelLoading = ref(false)
const riskActionError = ref('')
const riskActionSuccess = ref('')
const supervisedRecordingOpen = ref(false)

const completeForm = ref({
  arrivedLate: false,
  minutesLate: '',
  engagementKind: null,
  engagementBody: '',
})

const engagementItems = computed(() => [
  { title: t('appointments.completeEngagementNone'), value: null },
  ...ENGAGEMENT_KINDS.map(kind => ({
    title: observationKindLabel(kind),
    value: kind,
  })),
])

const formatTimeRange = item => {
  const opts = { hour: '2-digit', minute: '2-digit' }
  const start = formatDate(item.starts_at, opts, 'nl')
  const end = formatDate(item.ends_at, opts, 'nl')

  return `${start} – ${end}`
}

const formatWhen = appointment => {
  const date = formatDate(appointment.starts_at, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }, 'nl')

  return `${date}, ${formatTimeRange(appointment)}`
}

const canRecordWorkout = appointment =>
  !!appointment && [ 'confirmed', 'completed' ].includes(appointment.status)

const openSupervisedRecording = () => {
  formError.value = ''
  supervisedRecordingOpen.value = true
}

const onLoadError = () => {
  formError.value = store.error || t('appointments.loadError')
}

const openAppointment = appointment => {
  formError.value = ''
  selected.value = appointment
  editSessionFormat.value = appointment.session_format || 'physical'
  detailDialog.value = true
}

const saveSessionFormat = async () => {
  if (!selected.value || !canUpdateSessionFormat(selected.value))
    return

  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await store.updateSessionFormat(selected.value.id, editSessionFormat.value)
    selected.value = data.appointment
    formSuccess.value = t('appointments.formatUpdated')
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
}

const runOutcome = async (action, appointment) => {
  if (action === 'complete') {
    detailDialog.value = false
    openComplete(appointment)

    return
  }

  formError.value = ''
  formSuccess.value = ''
  try {
    await store[action](appointment.id)
    detailDialog.value = false
    formSuccess.value = t('appointments.actionSuccess')
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
}

const openComplete = appointment => {
  formError.value = ''
  formSuccess.value = ''
  selected.value = appointment
  completeForm.value = {
    arrivedLate: false,
    minutesLate: '',
    engagementKind: null,
    engagementBody: '',
  }
  completeDialog.value = true
}

const confirmComplete = async () => {
  if (!selected.value)
    return

  formError.value = ''
  formSuccess.value = ''
  try {
    await store.complete(selected.value.id, {
      arrivedLate: completeForm.value.arrivedLate,
      minutesLate: completeForm.value.arrivedLate ? completeForm.value.minutesLate : undefined,
      engagementKind: completeForm.value.engagementKind || undefined,
      engagementBody: completeForm.value.engagementBody?.trim() || undefined,
    })
    completeDialog.value = false
    formSuccess.value = t('appointments.completedSuccess')
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
}

const openCancel = async appointment => {
  formError.value = ''
  selected.value = appointment
  detailDialog.value = false
  cancelPreview.value = null
  cancelLoading.value = true
  cancelDialog.value = true
  try {
    cancelPreview.value = await store.cancellationPreview(appointment.id)
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
    cancelDialog.value = false
  }
  finally {
    cancelLoading.value = false
  }
}

const cancelBody = computed(() => {
  if (!selected.value || !cancelPreview.value)
    return t('appointments.cancelLoadingBody')

  const classification = classificationLabel(cancelPreview.value.classification)

  const consume = cancelPreview.value.consumes_session
    ? t('appointments.cancelConsumesYes')
    : t('appointments.cancelConsumesNo')

  return t('appointments.cancelBody', {
    when: formatWhen(selected.value),
    client: selected.value.client_name || t('appointments.unknownClient'),
    classification,
    consume,
  })
})

const confirmCancel = async () => {
  if (!selected.value)
    return
  formError.value = ''
  formSuccess.value = ''
  cancelLoading.value = true
  try {
    await store.cancel(selected.value.id)
    formSuccess.value = t('appointments.cancelledSuccess')
    cancelDialog.value = false
    selected.value = null
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
  finally {
    cancelLoading.value = false
  }
}

const openReschedule = async appointment => {
  formError.value = ''
  selected.value = appointment
  detailDialog.value = false
  rescheduleDialog.value = true
  try {
    const { from, to } = weekRangeIso(appointment.starts_at)

    await store.fetchOpenSlots({ from, to })
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
}

const pickRescheduleSlot = async slot => {
  if (!selected.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.reschedule(selected.value.id, slot.id)
    formSuccess.value = t('appointments.rescheduledSuccess')
    rescheduleDialog.value = false
    selected.value = null
  }
  catch {
    formError.value = store.error || t('appointments.actionError')
  }
}

const openRisk = async appointment => {
  riskActionError.value = ''
  riskActionSuccess.value = ''
  selected.value = appointment
  detailDialog.value = false
  selectedRisk.value = appointment.no_show_risk_assessment || null
  riskDialog.value = true

  const compact = appointment.no_show_risk_assessment
  if (!compact?.id)
    return

  try {
    selectedRisk.value = await riskStore.fetchAssessment(compact.id)
  }
  catch {
    riskActionError.value = riskStore.error || t('noShowRisk.actionError')
  }
}

const runRiskAction = async action => {
  if (!selectedRisk.value?.id)
    return

  riskActionError.value = ''
  riskActionSuccess.value = ''
  try {
    selectedRisk.value = await riskStore[action](selectedRisk.value.id)
    riskActionSuccess.value = t('noShowRisk.actionSuccess')
    await store.fetchAppointments()
  }
  catch {
    riskActionError.value = riskStore.error || t('noShowRisk.actionError')
  }
}

const selectedClientName = computed(() => {
  return selected.value?.client_name || t('appointments.unknownClient')
})

const riskFactors = computed(() => {
  const factors = selectedRisk.value?.contributing_factors

  return Array.isArray(factors) ? factors : []
})
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <h1 class="text-h4 mb-1">
          {{ $t('appointments.title') }}
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-0">
          {{ $t('appointments.subtitle') }}
        </p>
      </div>
    </div>

    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ formError }}
    </VAlert>
    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <AppointmentsCalendar
      @select="openAppointment"
      @load-error="onLoadError"
    />

    <VDialog
      v-model="detailDialog"
      max-width="560"
      aria-modal="true"
    >
      <VCard
        v-if="selected"
        flat
      >
        <VCardItem>
          <VCardTitle>{{ formatWhen(selected) }}</VCardTitle>
          <VCardSubtitle>{{ selectedClientName }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <div class="d-flex align-center ga-2 mb-4 flex-wrap">
            <span class="text-medium-emphasis">{{ $t('appointments.statusPrefix') }}</span>
            <VChip
              size="small"
              label
              variant="tonal"
              :color="appointmentStatusColor(selected.status)"
              :prepend-icon="selected.status === 'no_show' ? 'ri-user-unfollow-line' : 'ri-calendar-check-line'"
            >
              {{ appointmentStatusLabel(selected.status) }}
            </VChip>
            <span class="text-medium-emphasis">{{ $t('sessionFormat.prefix') }}</span>
            <VChip
              size="small"
              label
              variant="tonal"
              :color="sessionFormatColor(selected.session_format)"
            >
              {{ sessionFormatLabel(selected.session_format) }}
            </VChip>
            <StatusChip
              v-if="selected.no_show_risk_assessment"
              domain="no_show_risk"
              :state="selected.no_show_risk_assessment.risk_level"
              size="x-small"
            />
          </div>

          <div
            v-if="canUpdateSessionFormat(selected)"
            class="mb-2"
          >
            <fieldset class="appointments-format-fieldset">
              <legend
                id="appointment-session-format-legend"
                class="text-subtitle-2 mb-2"
              >
                {{ $t('appointments.formatEditLegend') }}
              </legend>
              <VRadioGroup
                v-model="editSessionFormat"
                inline
                aria-labelledby="appointment-session-format-legend"
                hide-details
                class="mb-2"
              >
                <VRadio
                  value="physical"
                  :label="$t('sessionFormat.physical')"
                />
                <VRadio
                  value="online"
                  :label="$t('sessionFormat.online')"
                />
              </VRadioGroup>
              <VBtn
                size="small"
                variant="tonal"
                color="primary"
                :loading="store.saving"
                :disabled="editSessionFormat === selected.session_format"
                :aria-label="$t('appointments.formatSaveAria', { client: selectedClientName })"
                @click="saveSessionFormat"
              >
                {{ $t('appointments.formatSave') }}
              </VBtn>
            </fieldset>
          </div>
        </VCardText>
        <VCardActions class="flex-wrap">
          <VBtn
            v-if="selected.no_show_risk_assessment"
            size="small"
            variant="tonal"
            :color="riskAlertType(selected.no_show_risk_assessment.risk_level)"
            :aria-label="$t('noShowRisk.openAria', { client: selectedClientName })"
            @click="openRisk(selected)"
          >
            {{ $t('noShowRisk.viewDetails') }}
          </VBtn>
          <VBtn
            v-if="canRecordWorkout(selected)"
            size="small"
            variant="tonal"
            color="secondary"
            :aria-label="$t('appointments.actions.recordWorkoutAria', { client: selectedClientName })"
            @click="openSupervisedRecording"
          >
            {{ $t('appointments.actions.recordWorkout') }}
          </VBtn>
          <VBtn
            v-if="canConfirm(selected)"
            size="small"
            variant="tonal"
            color="primary"
            :disabled="store.saving"
            :aria-label="$t('appointments.actions.confirmAria', { client: selectedClientName })"
            @click="runOutcome('confirm', selected)"
          >
            {{ $t('appointments.actions.confirm') }}
          </VBtn>
          <VBtn
            v-if="canComplete(selected)"
            size="small"
            variant="tonal"
            color="primary"
            :disabled="store.saving"
            :aria-label="$t('appointments.actions.completeAria', { client: selectedClientName })"
            @click="runOutcome('complete', selected)"
          >
            {{ $t('appointments.actions.complete') }}
          </VBtn>
          <VBtn
            v-if="canMarkNoShow(selected)"
            size="small"
            variant="tonal"
            color="warning"
            :disabled="store.saving"
            :aria-label="$t('appointments.actions.noShowAria', { client: selectedClientName })"
            @click="runOutcome('markNoShow', selected)"
          >
            {{ $t('appointments.actions.noShow') }}
          </VBtn>
          <VBtn
            v-if="canReschedule(selected)"
            size="small"
            variant="text"
            :disabled="store.saving"
            :aria-label="$t('appointments.actions.rescheduleAria', { client: selectedClientName })"
            @click="openReschedule(selected)"
          >
            {{ $t('appointments.actions.reschedule') }}
          </VBtn>
          <VBtn
            v-if="canCancel(selected)"
            size="small"
            variant="text"
            color="error"
            :disabled="store.saving"
            :aria-label="$t('appointments.actions.cancelAria', { client: selectedClientName })"
            @click="openCancel(selected)"
          >
            {{ $t('appointments.actions.cancel') }}
          </VBtn>
          <VSpacer />
          <VBtn
            variant="text"
            @click="detailDialog = false"
          >
            {{ $t('common.close') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model="cancelDialog"
      :title="$t('appointments.cancelTitle')"
      :body="cancelBody"
      :confirm-label="$t('appointments.cancelConfirm')"
      :cancel-label="$t('common.cancel')"
      color="error"
      :loading="cancelLoading || store.saving"
      @confirm="confirmCancel"
    />

    <VDialog
      v-model="completeDialog"
      max-width="520"
      aria-modal="true"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>{{ $t('appointments.completeTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="mb-4">
            {{ $t('appointments.completeBody', {
              when: selected ? formatWhen(selected) : '',
              client: selected?.client_name || $t('appointments.unknownClient'),
            }) }}
          </p>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>
          <VCheckbox
            v-model="completeForm.arrivedLate"
            :label="$t('appointments.completeArrivedLate')"
            class="mb-2"
          />
          <VTextField
            v-if="completeForm.arrivedLate"
            v-model="completeForm.minutesLate"
            type="number"
            min="1"
            :label="$t('appointments.completeMinutesLate')"
            :hint="$t('appointments.completeMinutesLateHint')"
            persistent-hint
            class="mb-4"
            autocomplete="off"
          />
          <VSelect
            v-model="completeForm.engagementKind"
            :items="engagementItems"
            item-title="title"
            item-value="value"
            :label="$t('appointments.completeEngagement')"
            clearable
            class="mb-4"
          />
          <VTextarea
            v-if="completeForm.engagementKind"
            v-model="completeForm.engagementBody"
            :label="$t('appointments.completeEngagementBody')"
            rows="2"
            autocomplete="off"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="store.saving"
            @click="completeDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="confirmComplete"
          >
            {{ $t('appointments.completeConfirm') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="rescheduleDialog"
      max-width="560"
      aria-modal="true"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>{{ $t('appointments.rescheduleTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="mb-4">
            {{ $t('appointments.rescheduleBody', {
              when: selected ? formatWhen(selected) : '',
              client: selected?.client_name || $t('appointments.unknownClient'),
            }) }}
          </p>
          <div
            v-if="!store.openSlots.length"
            class="text-medium-emphasis"
            role="status"
          >
            {{ $t('appointments.rescheduleEmpty') }}
          </div>
          <ul
            v-else
            class="appointments-slot-list"
          >
            <li
              v-for="slot in store.openSlots"
              :key="slot.id"
            >
              <VBtn
                block
                variant="tonal"
                color="primary"
                class="justify-start text-none mb-2"
                :disabled="store.saving"
                :aria-label="$t('appointments.rescheduleSlotAria', { time: formatTimeRange(slot) })"
                @click="pickRescheduleSlot(slot)"
              >
                {{ formatWhen(slot) }}
              </VBtn>
            </li>
          </ul>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="store.saving"
            @click="rescheduleDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="riskDialog"
      max-width="560"
      aria-modal="true"
      :aria-label="$t('noShowRisk.panelTitle')"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>{{ $t('noShowRisk.panelTitle') }}</VCardTitle>
          <VCardSubtitle>{{ $t('noShowRisk.panelSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <div
            v-if="riskStore.loading && !selectedRisk"
            role="status"
          >
            {{ $t('noShowRisk.loading') }}
          </div>

          <VAlert
            v-else-if="!selectedRisk"
            type="info"
            variant="tonal"
            role="status"
          >
            {{ $t('noShowRisk.empty') }}
          </VAlert>

          <template v-else>
            <VAlert
              v-if="riskActionError"
              type="error"
              variant="tonal"
              class="mb-4"
              role="alert"
            >
              {{ riskActionError }}
            </VAlert>
            <VAlert
              v-if="riskActionSuccess"
              type="success"
              variant="tonal"
              class="mb-4"
              role="status"
            >
              {{ riskActionSuccess }}
            </VAlert>
            <VAlert
              v-if="selectedRisk.stale"
              type="info"
              variant="tonal"
              class="mb-4"
              role="status"
            >
              {{ $t('noShowRisk.stale') }}
            </VAlert>

            <div class="d-flex flex-wrap align-center gap-2 mb-3">
              <StatusChip
                domain="no_show_risk"
                :state="selectedRisk.risk_level"
              />
              <span class="text-medium-emphasis">
                {{ $t(`noShowRisk.confidence.${selectedRisk.confidence}`) }}
              </span>
              <span class="text-caption text-medium-emphasis">
                {{ $t(`noShowRisk.lifecycle.${selectedRisk.lifecycle_state}`) }}
              </span>
            </div>

            <VAlert
              :type="riskAlertType(selectedRisk.risk_level)"
              variant="tonal"
              role="status"
              class="mb-4"
            >
              {{ selectedRisk.explanation }}
            </VAlert>

            <p
              v-if="riskFactors.length"
              class="text-subtitle-2 mb-2"
            >
              {{ $t('noShowRisk.factorsTitle') }}
            </p>
            <ul
              v-if="riskFactors.length"
              class="mb-0"
            >
              <li
                v-for="(factor, index) in riskFactors"
                :key="`${factor.code}-${index}`"
              >
                {{ factor.label }}
              </li>
            </ul>
          </template>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="riskStore.saving"
            @click="riskDialog = false"
          >
            {{ $t('common.close') }}
          </VBtn>
          <template v-if="selectedRisk && canRespondToRisk(selectedRisk)">
            <VBtn
              variant="tonal"
              :loading="riskStore.saving"
              :aria-label="$t('noShowRisk.actions.acknowledgeAria')"
              @click="runRiskAction('acknowledge')"
            >
              {{ $t('noShowRisk.actions.acknowledge') }}
            </VBtn>
            <VBtn
              variant="tonal"
              :loading="riskStore.saving"
              :aria-label="$t('noShowRisk.actions.dismissAria')"
              @click="runRiskAction('dismiss')"
            >
              {{ $t('noShowRisk.actions.dismiss') }}
            </VBtn>
            <VBtn
              color="primary"
              :loading="riskStore.saving"
              :aria-label="$t('noShowRisk.actions.actedUponAria')"
              @click="runRiskAction('markActedUpon')"
            >
              {{ $t('noShowRisk.actions.actedUpon') }}
            </VBtn>
          </template>
        </VCardActions>
      </VCard>
    </VDialog>

    <SupervisedWorkoutRecordingDialog
      v-model="supervisedRecordingOpen"
      :appointment="selected"
    />
  </div>
</template>

<style scoped>
.appointments-slot-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.appointments-format-fieldset {
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}
</style>
